<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { flatten422Errors, translateErrorMessages } from '../../services/api.svelte.js';
    import summaryService from './service.js';
    import { format } from 'svelte-i18n';

    const dispatch = createEventDispatcher();

    let { 
        formData = $bindable({}), 
        config = {}, 
        summaries = [], 
        onsave, 
        ondelete, 
        oncancel 
    } = $props();

    let formIsSubmitted = $state(false);
    let resetSummaries = $state(false);
    let APIerror = $state(null);
    let formApiErrors = $state({});

    const md = new window.markdownit({ html: false });

    let markdown = $state('');
    let result = $derived(md.render(markdown || ''));

    async function handleSubmit(e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        formIsSubmitted = true;
        formApiErrors = {};
        APIerror = null;

        formData.content = markdown;
        resetSummaries = !resetSummaries;

        try {
            const savedSummary = await summaryService.save(formData, [], config);
            formData = {};
            markdown = '';
            formIsSubmitted = false;
            formApiErrors = {};
            APIerror = null;

            dispatch("save", { savedSummary });
            onsave?.({ savedSummary });
        } catch (errors) {
            if (typeof errors?.detail === 'string') {
                APIerror = translateErrorMessages(errors.detail, $format("summmary"));
            } else {
                formApiErrors = flatten422Errors(errors);
            }
            formIsSubmitted = false;
        }
    }

    const getSummaryDetail = async (e) => {
        const selectedId = e.target.value;
        if (selectedId === 'none' || !selectedId) {
            formData = {};
            markdown = '';
            return;
        }

        try {
            let summary = await summaryService.find(selectedId);
            formData = {
                id: summary.id,
                name: summary.name || '',
                description: summary.description || '',
                content: summary.content || ''
            };
            markdown = formData.content || '';
        } catch (err) {
            console.error(err);
        }
    };

    function handleCloseModal() {
        if (config.add) {
            formData = {};
            formData.references = [];
        }
        markdown = '';
        formIsSubmitted = false;
        formApiErrors = {};
        APIerror = null;
        oncancel?.();
    }

    const handleKeydown = (event) => {
        if (event.key === "Escape") {
            handleCloseModal();
        }
    };

    const handleDelete = async () => {
        if (!formData.id || formData.id === 'none') return;
        try {
            let summary = { id: formData.id };
            await summaryService.del(summary);
            formData = {};
            markdown = '';
            dispatch("delete");
            ondelete?.();
        } catch (errors) {
            console.error(errors);
        }
    };
</script>

<svelte:window onkeydown={handleKeydown}/>

<form role="form" novalidate onsubmit={handleSubmit}>

    {#if config.edit}
        <div class="mb-3">
            <label for="summaryEditFormSummaries">{$format("summmary")}:</label>
            {#key resetSummaries}
                <select 
                    id="summaryEditFormSummaries" 
                    class="form-select"
                    bind:value={formData.id} 
                    onchange={getSummaryDetail}
                >
                    <option value="none">{$format("form.fields.select")}...</option>
                    {#each summaries as summary (summary.id)}
                        <option value={summary.id}>{summary.name} ({summary.description || ''})</option>
                    {/each}
                </select>
            {/key}
        </div>
    {/if}

    {#if config.delete}
        <div class="mb-3">
            <label for="summaryDeleteFormSummaries">{$format("summmary")}:</label>
            {#key resetSummaries}
                <select 
                    id="summaryDeleteFormSummaries" 
                    class="form-select"
                    bind:value={formData.id} 
                    onchange={getSummaryDetail}
                >
                    <option value="none">{$format("form.fields.select")}...</option>
                    {#each summaries as summary (summary.id)}
                        <option value={summary.id}>{summary.name} ({summary.description || ''})</option>
                    {/each}
                </select>
            {/key}
        </div>
    {/if}

    {#if config.add || config.edit}
        <div class="mb-3">
            <label for="summaryFormName">{$format("form.fields.name")}</label>
            <input
                type="text"
                class="form-control"
                id="summaryFormName"
                name="name"
                placeholder={$format("form.fields.name")}
                bind:value={formData.name}
            />
        </div>

        <div class="mb-3">
            <label for="summaryFormDesc">{$format("form.fields.description")}</label>
            <textarea
                class="form-control"
                id="summaryFormDesc"
                name="description"
                placeholder={$format("form.fields.description")}
                bind:value={formData.description}
            ></textarea>
        </div>

        <div class="markdown-editor">
            <div class="markdown-editor__panel">
                <span class="markdown-editor__panel__label">Markdown</span>
                <textarea id="summaryFormMarkdown" class="markdown-editor__textarea form-control" bind:value={markdown}></textarea>
            </div>
            <div class="markdown-editor__panel">
                <span class="markdown-editor__panel__label">{$format("preview")}</span>
                <div class="markdown-editor__result-html form-control">
                    {@html result}
                </div>
            </div>
        </div>
    {/if}

    <div class="mt-4 d-flex gap-2">
        {#if config.add || config.edit}
            <button id="SummaryAddSubmit" type="submit" class="btn btn-primary">{$format("buttons.save")}</button>
        {/if}

        {#if config.delete}
            <button id="SummaryDeleteBtn" type="button" class="btn btn-danger" onclick={handleDelete}>{$format("buttons.delete")}</button>
        {/if}

        <button type="button" class="btn btn-default" onclick={handleCloseModal} data-bs-dismiss="modal">{$format("buttons.cancel")}</button>
    </div>

</form>

<style>
    select {
        max-width: 400px;
    }
    .markdown-editor {
        display: flex;
        justify-content: space-between;
        box-sizing: border-box;
    }

    .markdown-editor__panel {
        width: calc(50% - 1rem);
        height: 400px;
        box-sizing: inherit;
    }

    .markdown-editor__panel__label {
        display: block;
        font-weight: 700;
        margin-bottom: 0.75rem;
    }

    .markdown-editor__textarea, .markdown-editor__result-html {
        box-sizing: inherit;
        height: 100%;
        width: 100%;
        padding: 1rem;
    }

    .markdown-editor__textarea {
        margin: 0;
    }

    .markdown-editor__result-html {
        overflow-y: scroll;
    }
</style>